import { useLocation, useSearchParams } from 'react-router-dom'
import { Column, Table } from '@tanstack/react-table'
import { entries, groupBy, keys } from 'lodash'
import { ChangeEvent, useEffect, useState } from 'react'
import { reorderColumn, moveColumnToEnd } from './columnUtils'
import { useSettings } from 'app/hooks/useSettings'
import { bg } from 'lib/background'
import type { ColumnEditorRequest } from 'app/types/column-editor'
import { onColumnEditorRequest } from 'lib/columnEditorRequest'
import { debug } from 'lib/logging'

type ColumnCookie = Record<string, { visible: boolean; order?: number }>

function isColumnCookie(value: unknown): value is ColumnCookie {
  return !!value && typeof value === `object` && !Array.isArray(value)
}

export type TableColumnSelectorProps<T> = {
  activeSubpage?: string
  table: Table<T>
  columnVisibility: { [k: string]: boolean }
  defaultColumnVisibility: { [k: string]: boolean }
  defaultColumnOrder: string[]
  onResetSizing?: () => void
  /** Draw no button of its own: something else opens the editor, through `requestColumnEditor`. */
  hideTrigger?: boolean
}

export function useTableColumnSelector<T>({
  activeSubpage,
  table,
  columnVisibility,
  defaultColumnVisibility,
  defaultColumnOrder,
  onResetSizing,
  hideTrigger,
}: TableColumnSelectorProps<T>) {
  const [openDialog, setOpenDialog] = useState(false)
  // Set only while a request that came with a way back (a list's Settings) has the editor open.
  const [back, setBack] = useState<ColumnEditorRequest['back']>()
  const [searchValue, setSearchValue] = useState(``)
  const [openGroups, setOpenGroups] = useState<number[]>([])
  const [shouldResetSizing, setShouldResetSizing] = useState(false)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [columnOrder, setColumnOrder] = useState(table.getState().columnOrder)
  const [newColumnVisibility, setNewColumnVisibility] = useState<typeof columnVisibility | undefined>()
  const visibilitySetting = `columnVisibility-${
    activeSubpage ? `${location.pathname}${activeSubpage}` : location.pathname
  }`
  const [settings, setSettings] = useSettings(visibilitySetting)

  useEffect(() => {
    setColumnOrder(table.getState().columnOrder)
  }, [table, table.getState().columnOrder])

  useEffect(
    () =>
      onColumnEditorRequest(location.pathname, (request) => {
        setBack(request.back)
        setOpenDialog(true)
      }),
    [location.pathname],
  )

  const handleReorderColumn = (draggedColumnId: string, targetColumnId: string) => {
    setColumnOrder((prev) => reorderColumn(prev, draggedColumnId, targetColumnId))
  }

  const onVisibilityChange = (columnId: string) => {
    setColumnOrder((prev) => {
      // TRK-4827: columns can be missing from columnOrder when they're added dynamically
      // (e.g. sensitive columns appearing after useFeatureToggle resolves async).
      // SimpleTable doesn't update columnOrder when columns prop changes if sizing settings exist.
      if (!prev.includes(columnId)) {
        return [...prev, columnId]
      }
      return moveColumnToEnd(prev, columnId)
    })
    setNewColumnVisibility((prev) => ({ ...prev, [columnId]: !prev?.[columnId] }))
  }

  const handleApplyClick = async () => {
    setOpenDialog(false)
    setBack(undefined)
    table.setColumnOrder([...columnOrder])
    table.setColumnVisibility({
      ...columnOrder.reduce((r, c) => Object.assign(r, { [c]: false }), {}),
      ...newColumnVisibility!,
    })
    if (shouldResetSizing) {
      onResetSizing?.()
      setShouldResetSizing(false)
    }
    let current: ColumnCookie = isColumnCookie(settings) ? settings : {}
    let update = { ...current }
    entries(newColumnVisibility).forEach(
      ([key, val]) => (update[key] = { visible: val, order: columnOrder.indexOf(key) }),
    )
    keys(update).forEach((key) => {
      update[key].order = columnOrder.indexOf(key) >= 0 ? columnOrder.indexOf(key) : update[key].order
    })

    // don't save columns to the user if we're on the scorecards page
    if (!location.pathname.includes(`scorecards`)) {
      debug(`columns`, `saving columns`, { key: visibilitySetting, orderSample: columnOrder.slice(0, 8), update })
      await setSettings(visibilitySetting, update)
    }
  }

  useEffect(() => {
    bg(async () => {
      const savedColumns = settings
      debug(`columns`, `load effect`, { hasSavedColumns: !!savedColumns, pathname: location.pathname, settings })
      // don't load columns from user settings if we're on the scorecards page
      if (savedColumns && !location.pathname.includes(`scorecards`)) {
        // TRK-4844: merge defaultColumnVisibility (has all current columns) with columnVisibility
        // (may be missing dynamically-added columns).
        const allColumnVisibility = { ...defaultColumnVisibility, ...columnVisibility }
        // A column the saved layout does not list (added after the layout was saved) takes its
        // default, not the current table state. When the layout is already cached (a tab opened
        // by an in-app click) this effect runs at mount, while useTableLayout still holds its
        // initial all-hidden state, so the current state would hide the column on a click and
        // show it on a fresh load. Only a column with no default keeps its current state.
        const columns = entries(allColumnVisibility).reduce(
          (acc, [key, val]) => {
            acc[key] = savedColumns[key]?.visible ?? defaultColumnVisibility[key] ?? val ?? true
            return acc
          },
          {} as Record<string, boolean>,
        )
        // TRK-4827: copy before sorting — .sort() mutates in place and the original
        // code was mutating table.getState().columnOrder directly (internal state).
        let columnOrder: string[] = [...table.getState().columnOrder]

        // TRK-4844: append columns that exist in the current column definitions
        // but are missing from the table's column order (added dynamically after initial render).
        for (const id of defaultColumnOrder) {
          if (!columnOrder.includes(id)) columnOrder.push(id)
        }

        // Snapshot original positions before sorting — indexOf on a mutating array is unreliable.
        const originalIndex = new Map(columnOrder.map((id, i) => [id, i]))
        columnOrder.sort((left, right) => {
          let leftI = savedColumns[left]?.order ?? originalIndex.get(left)!
          let rightI = savedColumns[right]?.order ?? originalIndex.get(right)!
          return leftI - rightI
        })
        debug(`columns`, `restored order`, columnOrder.slice(0, 8))

        table.setColumnOrder([...columnOrder])
        setNewColumnVisibility(columns)
        table.setColumnVisibility(columns)
      } else {
        setNewColumnVisibility({ ...defaultColumnVisibility })
        table.setColumnVisibility(defaultColumnVisibility)
      }
    })
  }, [location.pathname, searchParams.get(`tab`), settings, defaultColumnOrder.join()])

  const filterGroups = groupBy(
    table?.getAllColumns().filter((item) => !item.columnDef.meta?.hideInColumnSelector),
    (item) => item.columnDef.meta?.group?.trim() ?? `Columns`,
  )

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    let searchValue = event.target.value
    setSearchValue(searchValue)
    if (searchValue === ``) return setOpenGroups([])
    let groups: number[] = []
    Object.entries(filterGroups).forEach(([, filters], i) => {
      if (filters.some((col) => col.columnDef.header?.toString().toLowerCase().includes(searchValue.toLowerCase()))) {
        groups.push(i)
      }
    })
    setOpenGroups(groups)
  }

  const handleResetToDefault = () => {
    setColumnOrder([...defaultColumnOrder])
    setNewColumnVisibility({ ...defaultColumnVisibility })
    setShouldResetSizing(true)
  }

  const handleClose = () => {
    setOpenDialog(false)
    setBack(undefined)
    setSearchValue(``)
    setColumnOrder(table.getState().columnOrder)
    setNewColumnVisibility(table.getState().columnVisibility)
    setShouldResetSizing(false)
  }

  const selectedColumns = columnOrder
    .map((item) => table.getAllLeafColumns().find((col) => col.id === item))
    .filter((col): col is Column<T, unknown> => !!col?.id && !!newColumnVisibility?.[col.id])

  // Back discards unapplied edits, like Cancel, then returns to whoever opened the editor.
  const backTo = back && {
    to: back.to,
    onBack: () => {
      handleClose()
      back.onBack()
    },
  }

  return {
    hideTrigger,
    backTo,
    openDialog,
    setOpenDialog,
    searchValue,
    openGroups,
    filterGroups,
    columnOrder,
    newColumnVisibility,
    selectedColumns,
    handleReorderColumn,
    onVisibilityChange,
    handleApplyClick,
    handleSearch,
    handleResetToDefault,
    handleClose,
    setNewColumnVisibility,
  }
}
